import React, { useMemo, useState } from "react";
import {
  Pencil,
  Trash2,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminRole } from "../../context/AdminUserContext";

const PAGE_SIZE = 8;
const CATEGORIES = ["News", "Tech", "Events"];

// Show a date like "12 Mar 2025".
const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Tags may arrive as an array, a JSON string or a comma string.
const parseTags = (tags) => {
  if (Array.isArray(tags)) return tags;
  if (!tags) return [];
  try {
    const parsed = JSON.parse(tags);
    if (Array.isArray(parsed)) return parsed;
  } catch (e) {
    /* not JSON */
  }
  return String(tags)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
};

// A blog is soft-deleted when the API includes a deleted_at timestamp.
const isDeleted = (blog) => !!blog.deleted_at;

// A blog is active whether the API sends true, 1, "1" or "active".
const isActive = (blog) =>
  blog.is_active === true ||
  blog.is_active === 1 ||
  blog.is_active === "1" ||
  blog.is_active === "active";

const BlogsTable = ({
  blogs = [],
  loading = false,
  onToggleStatus,
  onEdit,
  onDelete,
}) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  // Column sort — newest first by default.
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  // Only admins see the Status column and the delete action. Role comes from
  // the authenticated user (via /check-auth), not localStorage.
  const isAdmin = useAdminRole() === "admin";
  const showStatus = isAdmin;

  // Columns shown — drives the loading/empty-row colSpan.
  const colCount = showStatus ? 6 : 5;

  // Category options = the known set + anything else found in the data.
  const categoryOptions = useMemo(() => {
    const set = new Set(CATEGORIES);
    blogs.forEach((b) => {
      const c = b.blog_category || b.category;
      if (c) set.add(c);
    });
    return [...set];
  }, [blogs]);

  // Filter by search query + category.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blogs.filter((b) => {
      const title = (b.blog_title || b.title || "").toLowerCase();
      const cat = b.blog_category || b.category || "";
      const matchesQuery =
        !q || title.includes(q) || cat.toLowerCase().includes(q);
      const matchesCategory = category === "all" || cat === category;
      return matchesQuery && matchesCategory;
    });
  }, [blogs, query, category]);

  // Value used for comparing two rows on the active sort column.
  const sortValue = (blog, key) => {
    switch (key) {
      case "title":
        return (blog.blog_title || blog.title || "").toLowerCase();
      case "category":
        return (blog.blog_category || blog.category || "").toLowerCase();
      case "date":
        return new Date(blog.blog_date || blog.created_at || 0).getTime();
      case "status":
        return isActive(blog) ? 1 : 0;
      default:
        return "";
    }
  };

  // Sort the filtered rows by the active column + direction.
  const sorted = useMemo(() => {
    const rows = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Click a header: flip direction if it's the active column, else sort it ascending.
  const toggleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
    setPage(1);
  };

  const sortIcon = (key) => {
    if (sort.key !== key)
      return <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />;
    return sort.dir === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setPage(1);
  };

  const handleCategory = (value) => {
    setCategory(value);
    setPage(1);
  };

  const thClass =
    "text-gray-500 font-semibold uppercase text-[11px] tracking-wider";

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={query}
            onChange={handleSearch}
            placeholder="Search by title or category…"
            className="w-full rounded-md border border-gray-200 bg-gray-50 pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15 focus:outline-none transition"
          />
        </div>

        <Select value={category} onValueChange={handleCategory}>
          <SelectTrigger className="w-[180px] h-auto rounded-md border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-[#b32126] focus:bg-white focus:ring-2 focus:ring-[#b32126]/15">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categoryOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto shrink-0 text-sm text-gray-500">
          {filtered.length} {filtered.length === 1 ? "blog" : "blogs"}
        </span>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/80 hover:bg-gray-50/80 border-b border-gray-200">
            <TableHead className={`${thClass} pl-6`}>
              <button
                type="button"
                onClick={() => toggleSort("title")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Title
                {sortIcon("title")}
              </button>
            </TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("category")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Category
                {sortIcon("category")}
              </button>
            </TableHead>
            <TableHead className={thClass}>Tags</TableHead>
            <TableHead className={thClass}>
              <button
                type="button"
                onClick={() => toggleSort("date")}
                className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                Date
                {sortIcon("date")}
              </button>
            </TableHead>
            {showStatus && (
              <TableHead className={thClass}>
                <button
                  type="button"
                  onClick={() => toggleSort("status")}
                  className="inline-flex items-center gap-1 hover:text-gray-700 transition-colors"
                >
                  Status
                  {sortIcon("status")}
                </button>
              </TableHead>
            )}
            <TableHead className={`${thClass} text-right pr-6`}>
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={colCount} className="text-center text-gray-500 py-12">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading blogs…
                </span>
              </TableCell>
            </TableRow>
          ) : pageItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colCount} className="py-12">
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <FileText className="h-8 w-8" />
                  <span className="text-sm">
                    {query || category !== "all"
                      ? "No blogs match your filters."
                      : "No blogs yet. Click “Add Blog” to create one."}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            pageItems.map((blog) => {
              const title = blog.blog_title || blog.title || "Untitled";
              const cat = blog.blog_category || blog.category;
              const date = blog.blog_date || blog.created_at;
              const active = isActive(blog);
              const deleted = isDeleted(blog);
              const tags = parseTags(blog.blog_tags ?? blog.tags);
              const contentHtml = blog.blog_description || blog.content;

              return (
                <TableRow
                  key={blog.id}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60"
                >
                  {/* Title — hover to see the full blog */}
                  <TableCell className="font-medium text-gray-900 pl-6 py-4">
                    <HoverCard openDelay={80} closeDelay={60}>
                      <HoverCardTrigger asChild>
                        <span className="cursor-pointer hover:text-[#b32126] transition-colors">
                          {title}
                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent
                        align="start"
                        side="right"
                        className="w-[34rem] max-w-[90vw] p-0 overflow-hidden"
                      >
                        <div className="max-h-[65vh] overflow-y-auto">
                          <div className="p-5 border-b border-gray-100">
                            <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
                              {cat && (
                                <span className="inline-flex items-center rounded-full bg-[#b32126]/10 px-2.5 py-0.5 font-medium text-[#b32126]">
                                  {cat}
                                </span>
                              )}
                              {date && (
                                <span className="text-gray-400">
                                  {formatDate(date)}
                                </span>
                              )}
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                                  active
                                    ? "bg-green-50 text-green-700"
                                    : "bg-gray-100 text-gray-500"
                                }`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${
                                    active ? "bg-green-500" : "bg-gray-400"
                                  }`}
                                />
                                {active ? "Active" : "Inactive"}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 leading-snug">
                              {title}
                            </h3>
                            {tags.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-3">
                                {tags.map((t) => (
                                  <span
                                    key={t}
                                    className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                                  >
                                    #{t}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="p-5">
                            {contentHtml ? (
                              <div
                                className="prose prose-sm max-w-none text-gray-800"
                                dangerouslySetInnerHTML={{ __html: contentHtml }}
                              />
                            ) : (
                              <p className="text-sm text-gray-400">
                                No content for this blog.
                              </p>
                            )}
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </TableCell>

                  {/* Category */}
                  <TableCell className="py-4">
                    {cat ? (
                      <span className="inline-flex items-center rounded-full bg-[#b32126]/10 px-2.5 py-0.5 text-xs font-medium text-[#b32126]">
                        {cat}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Tags */}
                  <TableCell className="py-4">
                    {tags.length ? (
                      <div className="flex flex-wrap gap-1">
                        {tags.slice(0, 2).map((t) => (
                          <span
                            key={t}
                            className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                          >
                            {t}
                          </span>
                        ))}
                        {tags.length > 2 && (
                          <span className="text-xs text-gray-400">
                            +{tags.length - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>

                  {/* Date */}
                  <TableCell className="text-gray-600 py-4">
                    {formatDate(date)}
                  </TableCell>

                  {/* Status — toggle, or a Deleted badge for soft-deleted blogs */}
                  {showStatus && (
                    <TableCell className="py-4">
                      {deleted ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-600">
                          <Trash2 className="h-3 w-3" />
                          Deleted
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onToggleStatus?.(blog)}
                          title={active ? "Active" : "Inactive"}
                          className={`relative h-6 w-11 rounded-full transition-colors ${
                            active ? "bg-green-500" : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              active ? "translate-x-5" : ""
                            }`}
                          />
                        </button>
                      )}
                    </TableCell>
                  )}

                  {/* Actions — icon only (no delete for soft-deleted blogs) */}
                  <TableCell className="py-4 pr-6">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        title="Edit"
                        onClick={() => onEdit?.(blog)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {isAdmin && !deleted && (
                        <Button
                          variant="destructive"
                          size="icon"
                          title="Delete"
                          onClick={() => onDelete?.(blog)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-medium transition-colors ${
                  p === currentPage
                    ? "bg-[#b32126] text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() => setPage(currentPage + 1)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogsTable;
